import { interpolateText, parseTemplateVariables } from "@tellescope/utils";
import type { Enduser, FormField } from "@tellescope/types-models";
import type { TellescopeFieldResponse } from "../types";

/** Identity values a Redirect field's target URL may template in as bare `{{key}}` tokens */
const TEMPLATE_KEYS = ["id", "email", "fname", "lname", "state", "phone"] as const;

type TemplateKey = (typeof TEMPLATE_KEYS)[number];

/**
 * `{{URL_PARAM.name}}` resolves from the current query string rather than any record, so
 * {@link interpolateText} — which knows only the `enduser`/`organization`/`snippet` sources — never
 * sees it.
 */
const URL_PARAM_SOURCE = "URL_PARAM";

export interface ResolveRedirectUrlArgs {
  externalUrl: string;
  enduser: Partial<Enduser> & { id?: string };
  responses: TellescopeFieldResponse[];
  fieldsMap: Record<string, Pick<FormField, "intakeField">>;
  currentParams?: URLSearchParams;
}

const findAnsweredIntakeValue = (
  key: TemplateKey,
  responses: TellescopeFieldResponse[],
  fieldsMap: Record<string, Pick<FormField, "intakeField">>
): string | undefined => {
  const answered = responses.filter((r) => {
    const intakeField = fieldsMap[r.fieldId]?.intakeField;
    if (intakeField !== key) return false;
    return typeof r.answer?.value === "string" && r.answer.value !== "";
  });
  const latest = answered[answered.length - 1]?.answer?.value;
  return typeof latest === "string" ? latest : undefined;
};

/**
 * The enduser record overlaid with the identity values the form itself answered — the object legacy
 * substituted `{{enduser.*}}` against (`{...session.userInfo, id, email, fname, lname, state,
 * phone}`, `inputs.tsx:4900-4907`). Without the overlay, a form that collects an email and echoes
 * `{{enduser.email}}` onto the target would send the stale record value.
 */
const overlayAnsweredIdentity = ({
  enduser,
  responses,
  fieldsMap,
}: Pick<ResolveRedirectUrlArgs, "enduser" | "responses" | "fieldsMap">): Partial<Enduser> =>
  TEMPLATE_KEYS.reduce<Partial<Enduser>>(
    (acc, key) => {
      const answered = findAnsweredIntakeValue(key, responses, fieldsMap);
      return answered === undefined ? acc : { ...acc, [key]: answered };
    },
    { ...enduser }
  );

const CREDENTIAL_PARAM_PATTERN =
  /^(a|token|.*authtoken|accesscode|access_token|.*sessiontoken|resettoken|reset_token|apikey|api_key|password|secret)$/i;

/**
 * Attribution params carried from the current URL onto a redirect target, matching legacy's
 * `append_current_utm_params`.
 *
 * Two layers on purpose. The allowlist decides what is eligible — anything not named here is not an
 * arbitrary customer-configured target's business. The credential denylist is a backstop that must
 * hold even if the allowlist is later widened: the form URL carries the access code and auth token,
 * so a credential-shaped name must never become forwardable by an edit one line above.
 */
const isForwardableParam = (key: string): boolean =>
  (key.toLowerCase().startsWith("utm_") || key === "ours_user_id") && !CREDENTIAL_PARAM_PATTERN.test(key);

/**
 * Normalizes a configured redirect target to something safe to hand to `window.location`.
 *
 * A scheme-less value gains `https://`. Without this the browser resolves it relative to the
 * current document, so `example.com/done` configured on the `/link/form` deeplink navigates to
 * `<portal-url>/link/example.com/done` instead of leaving the portal.
 *
 * Returns `undefined` for a scheme that is neither http nor https, so callers can decline to
 * navigate rather than following a `javascript:` or `data:` target.
 */
export const toAbsoluteUrl = (url: string): string | undefined => {
  if (url.startsWith("//")) return url;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) return `https://${url}`;
  return /^https?:/i.test(url) ? url : undefined;
};

/**
 * Substitutes every template family a Redirect target may carry, matching legacy's two-stage
 * `include_current_url_parameters_if_templated(replace_enduser_template_values(url, …))`:
 *
 * - the six bare `{{id}}`/`{{email}}`/… identity tokens,
 * - arbitrary `{{enduser.FIELD}}` tokens — including custom fields, dotted paths, and the computed
 *   `BMI`/`Age` — resolved against the answered-identity overlay,
 * - `{{URL_PARAM.name}}` from the current query string, empty when the param is absent.
 *
 * Any other `{{source.field}}` is left verbatim, as legacy did — only these families were ever
 * substituted into a redirect target.
 *
 * Values are `encodeURIComponent`-escaped, which legacy did not do (see the redirect deviations):
 * these are enduser-supplied and land in a URL handed to `window.location`.
 */
const substituteTemplateValues = ({
  externalUrl,
  enduser,
  responses,
  fieldsMap,
  currentParams,
}: ResolveRedirectUrlArgs): string => {
  const identityEnduser = overlayAnsweredIdentity({ enduser, responses, fieldsMap });

  const withBareTokens = TEMPLATE_KEYS.reduce((url, key) => {
    const token = `{{${key}}}`;
    if (!url.includes(token)) return url;

    const value = identityEnduser[key];
    return url.split(token).join(encodeURIComponent(typeof value === "string" ? value : ""));
  }, externalUrl);

  return parseTemplateVariables(withBareTokens).reduce((url, variable) => {
    if (variable.source === URL_PARAM_SOURCE) {
      return url.split(variable.raw).join(encodeURIComponent(currentParams?.get(variable.field) ?? ""));
    }
    if (variable.source !== "enduser") return url;

    const resolved = interpolateText(variable.raw, {
      enduser: identityEnduser,
      organization: null,
      snippets: {},
      responses,
    });
    return url.split(variable.raw).join(encodeURIComponent(resolved));
  }, withBareTokens);
};

export const resolveRedirectUrl = (args: ResolveRedirectUrlArgs): string | undefined => {
  const { externalUrl, currentParams } = args;

  const substituted = substituteTemplateValues(args);

  const absolute = toAbsoluteUrl(substituted);
  if (!absolute) return undefined;

  if (
    substituted === externalUrl ||
    !currentParams ||
    Array.from(currentParams).length === 0 ||
    absolute.startsWith("//")
  ) {
    return absolute;
  }

  const target = new URL(absolute);
  currentParams.forEach((value, key) => {
    if (!isForwardableParam(key)) return;
    if (!target.searchParams.has(key)) target.searchParams.set(key, value);
  });
  return target.toString();
};
